import {
  PERCEPTION_MODELS,
  PERCEPTION_MODEL_TRAIN,
} from 'constants/routePaths';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  NormSetsForJobQuery,
  PerceptionModelJobByIdQuery,
  type PerceptionModelJobOption,
  type ProjectNormSetRow,
} from '@graphql/queries/PerceptionModelQueries';
import { ArrowBack } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';

import { DIMENSION_LABEL, levelLabel, type DemoDimension } from './demographic';
import PerceptionModelError from './PerceptionModelError';
import { formatDay, shortVersion } from './PerceptionModelPicker';
import { PageHeader, PagePaper, PageTitle } from '../../styles/themeComponents';

/**
 * One perception model, read only.
 *
 * Every other page in this flow names a model by a number — "job 3" — and then
 * asks the analyst to trust it. This is where that number resolves into what it
 * actually is: when it was trained, what corpus it saw, how many products came
 * out of it, and which demographic levels it will accept.
 *
 * That last one is the reason this page is worth having. `params.demo_levels`
 * is the only record of what the model was trained on, and a translation naming
 * a level outside it fails in R rather than rendering something plausible. Up
 * to now it was readable only by querying the row by hand.
 *
 * Nothing here is editable. A model is the output of a training run — a
 * different model is a new run, which is what the training page is for.
 */

type Props = { jobId: number };

/** Same vocabulary as the run history, so a status means one thing. */
const STATUS_COLOUR: Record<string, 'default' | 'info' | 'success' | 'error'> =
  {
    REQUESTED: 'default',
    STARTED: 'info',
    IN_PROGRESS: 'info',
    SUCCESS: 'success',
    FAILED: 'error',
  };

/** A row of the "what is this" table: label on the left, fact on the right. */
const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <TableRow>
    <TableCell sx={{ width: 200, color: 'text.secondary' }}>{label}</TableCell>
    <TableCell>{children}</TableCell>
  </TableRow>
);

/** How long the run took, when it got far enough to have taken any time. */
const elapsed = (startedAt?: string | null, finishedAt?: string | null) => {
  if (!startedAt || !finishedAt) return null;
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const hours = Math.floor(ms / 3.6e6);
  const minutes = Math.round((ms % 3.6e6) / 6e4);
  return hours ? `${hours}h ${minutes}m` : `${minutes}m`;
};

/**
 * The levels this model will accept, per dimension.
 *
 * Absent `demo_levels` is not "none" — it is an export that predates the field,
 * and saying "no levels" about it would read as a broken model.
 */
const TrainedLevels: React.FC<{
  levels?: Record<string, string[]> | null;
}> = ({ levels }) => {
  const entries = Object.entries(levels ?? {}).filter(
    ([, v]) => Array.isArray(v) && v.length > 0,
  );

  if (entries.length === 0) {
    return (
      <Alert severity="warning">
        This run recorded no <code>demo_levels</code>, so there is nothing here
        saying which levels it will accept. A translation against it is checked
        by R at render time instead. Re-run the export for this model to record
        them.
      </Alert>
    );
  }

  return (
    <Stack spacing={1}>
      {entries.map(([dimension, values]) => (
        <Box key={dimension}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {DIMENSION_LABEL[dimension as DemoDimension] ?? dimension}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
            {values.map((v) => (
              <Chip
                key={v}
                size="small"
                variant="outlined"
                label={levelLabel(v)}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

/**
 * What in this workspace depends on the model. Its own component so the page
 * body stays readable — and so the empty case is stated once.
 */
const NormSetsOnModel: React.FC<{ normSets: ProjectNormSetRow[] }> = ({
  normSets,
}) => {
  const history = useHistory();

  return (
    <Box>
      <Typography variant="subtitle1">Norm sets built on this model</Typography>
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
      >
        {normSets.length === 0
          ? 'None in this workspace yet.'
          : `${normSets.length} in this workspace. Each renders against this model rather than whatever is deployed now.`}
      </Typography>

      {normSets.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Norm set</TableCell>
              <TableCell align="right">Products</TableCell>
              <TableCell align="right">Anchors</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {normSets.map((n) => (
              <TableRow key={n.id} hover>
                <TableCell>
                  <Button
                    size="small"
                    onClick={() => history.push(`${PERCEPTION_MODELS}/${n.id}`)}
                    sx={{ textTransform: 'none' }}
                  >
                    {n.name}
                  </Button>
                </TableCell>
                <TableCell align="right">{(n.normSet ?? []).length}</TableCell>
                <TableCell align="right">{(n.anchors ?? []).length}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

const PerceptionModelDetail: React.FC<Props> = ({ jobId }) => {
  const history = useHistory();
  const producerId = useSelector((state) => selectWorkspaceProducerId(state));

  const { data, loading, error } = useQuery(PerceptionModelJobByIdQuery, {
    variables: { id: jobId },
  });

  const job: PerceptionModelJobOption | null =
    data?.allPerceptionModelJobs?.nodes?.[0] ?? null;

  // What in this workspace depends on the model. Scoped by producerId, unlike
  // the job itself: norm sets are per-workspace and carry no row-level
  // security, so the condition is what keeps one workspace's out of another's.
  const { data: normSetData } = useQuery(NormSetsForJobQuery, {
    variables: { jobId, producerId },
    skip: !producerId || !job,
  });
  const normSets: ProjectNormSetRow[] =
    normSetData?.allPerceptionModelProjects?.nodes ?? [];

  if (loading) return <PagePaper>Loading model…</PagePaper>;

  if (error) {
    return (
      <PagePaper>
        <PerceptionModelError error={error} />
      </PagePaper>
    );
  }

  if (!job) {
    return (
      <PagePaper>
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => history.push(PERCEPTION_MODEL_TRAIN)}
            >
              Runs
            </Button>
          }
        >
          No perception model #{jobId}.
        </Alert>
      </PagePaper>
    );
  }

  const products =
    job.perceptionModelProductLookupsByPerceptionModelJobId?.totalCount ?? 0;
  const took = elapsed(job.startedAt, job.finishedAt);
  const status = job.status ?? 'SUCCESS';

  return (
    <PagePaper>
      <Button
        size="small"
        startIcon={<ArrowBack fontSize="small" />}
        onClick={() => history.push(PERCEPTION_MODEL_TRAIN)}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        Perception models
      </Button>

      <PageHeader>Perception model</PageHeader>
      {/* The date, not the uuid. The hash distinguishes two runs on one day and
          is otherwise forty characters of noise — it is on the row below for
          anyone who needs to match it against a file on EFS. */}
      <PageTitle>{shortVersion(job.versionId)}</PageTitle>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          size="small"
          label={status}
          color={STATUS_COLOUR[status] ?? 'default'}
          variant={status === 'REQUESTED' ? 'outlined' : 'filled'}
        />
        {job.isDeployed && (
          <Chip size="small" color="primary" label="deployed" />
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Job #{job.id} · {job.universe}
        </Typography>
      </Box>

      {status === 'FAILED' && (
        <Alert severity="error" sx={{ mt: 1 }}>
          This run failed{job.statusReason ? `: ${job.statusReason}` : '.'}
        </Alert>
      )}

      {products === 0 && status !== 'FAILED' && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          This model exported no products, so it can neither anchor nor rank
          anything. A run that has not finished, or one whose export never ran.
        </Alert>
      )}

      <Stack spacing={2} sx={{ mt: 2 }}>
        <Box>
          <Typography variant="subtitle1">What this model is</Typography>
          <Table size="small">
            <TableBody>
              <Fact label="Version">
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {job.versionId}
                </Typography>
              </Fact>
              <Fact label="Universe">{job.universe}</Fact>
              <Fact label="Reviews pulled">
                {/* Anything reviewed after this date is not in the model,
                    which is the usual reason a product is missing from it. */}
                {job.pullDate ? formatDay(job.pullDate) : '—'}
              </Fact>
              <Fact label="Products exported">{products.toLocaleString()}</Fact>
              {took && <Fact label="Training took">{took}</Fact>}
            </TableBody>
          </Table>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle1">Levels it was trained on</Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
          >
            A translation naming anything outside these fails at render rather
            than coming back as something plausible.
          </Typography>
          <TrainedLevels levels={job.params?.demo_levels} />
        </Box>

        <Divider />

        <NormSetsOnModel normSets={normSets} />
      </Stack>
    </PagePaper>
  );
};

export default PerceptionModelDetail;
